import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// slice types
type ProjectType = {
  id: ID;
  name: string;
};
type TaskType = {
  id: ID;
  name: string;
};
export type AnchorPositionType = {
  top: number;
  left: number;
  triggerSource: string; // could be from 'navigation' or 'projectDetail' or ''
};
export type TimerDetailsType = {
  timeStarted: number | null;
  isRunning: boolean;
};
export type TimeTrackerMainStateType = {
  [userId: ID]: {
    project: ProjectType;
    task: TaskType;
    timerDetail: TimerDetailsType;
    anchorPosition: AnchorPositionType;
    isPopoverOpen: boolean;
    hasActiveProject: boolean;
    auditId: ID | null;
  };
};

// reducers payload types
export type TimeTrackerPayloadType = {
  userId: ID;
  projectId?: ID;
  projectName?: string;
  taskId?: ID;
  taskName?: string;
  auditId?: ID;
  timeStarted?: number;
};
export type PopoverPayloadType = TimeTrackerPayloadType & {
  anchorPosition: AnchorPositionType;
};

// UseTimeTrackerType selector types
export type UseTimeTrackerStateType = {
  projectId: ID;
  projectName: string;
  taskId: ID;
  taskName: string;
  isPopoverOpen: boolean;
  hasActiveProject: boolean;
  anchorPosition: AnchorPositionType | null;
  isRunning: boolean;
  timeStarted: number | null;
  auditId: ID;
};
export type UseTimeTrackerActionsType = {
  startTimer: (auditId: ID, timeStarted: number) => void;
  endTimer: () => void;
  setProject: (projectId: ID, projectName: string) => void;
  setTask: (taskId: ID, taskName: string) => void;
  openPopoverAt: (anchor: AnchorPositionType) => void;
  closePopover: () => void;
};

const initialState: TimeTrackerMainStateType = {};
const anchorPositionInitialState: AnchorPositionType = { top: 0, left: 0, triggerSource: '' };
const projectInitialState: ProjectType = { id: null, name: '' };
const taskInitialState: TaskType = { id: null, name: '' };
const timerDetailInitialState: TimerDetailsType = {
  timeStarted: null,
  isRunning: false,
};

const timeTrackerSlice = createSlice({
  name: 'timeTracker',
  initialState,
  reducers: {
    startTimer: (state, action: PayloadAction<TimeTrackerPayloadType>) => {
      const { userId, auditId, timeStarted } = action.payload;

      state[userId].hasActiveProject = true;
      state[userId].timerDetail.timeStarted = timeStarted;
      state[userId].timerDetail.isRunning = true;
      state[userId].auditId = auditId;
    },
    endTimer: (state, action: PayloadAction<TimeTrackerPayloadType>) => {
      const { userId } = action.payload;

      state[userId].project = projectInitialState;
      state[userId].task = taskInitialState;
      state[userId].timerDetail = timerDetailInitialState;
      state[userId].hasActiveProject = false;
      state[userId].auditId = null;
    },
    setProject: (state, action: PayloadAction<TimeTrackerPayloadType>) => {
      const { userId, projectId, projectName } = action.payload;

      if (!state[userId].timerDetail.isRunning) {
        if (!state[userId].project) {
          state[userId].project = projectInitialState;
        }

        state[userId].project.id = projectId;
        state[userId].project.name = projectName;
      }
    },
    setTask: (state, action: PayloadAction<TimeTrackerPayloadType>) => {
      const { userId, taskId, taskName } = action.payload;

      if (!state[userId].timerDetail.isRunning) {
        if (!state[userId].task) {
          state[userId].task = taskInitialState;
        }

        state[userId].task.id = taskId;
        state[userId].task.name = taskName;
      }
    },
    openPopoverAt: (state, action: PayloadAction<PopoverPayloadType>) => {
      const { userId, anchorPosition } = action.payload;

      if (!state[userId]) {
        state[userId] = {
          isPopoverOpen: true,
          hasActiveProject: false,
          auditId: null,
          project: projectInitialState,
          task: taskInitialState,
          timerDetail: timerDetailInitialState,
          anchorPosition: anchorPositionInitialState,
        };
      }

      state[userId].anchorPosition = anchorPosition;
      state[userId].isPopoverOpen = true;
    },
    closePopover: (state, action: PayloadAction<TimeTrackerPayloadType>) => {
      const { userId } = action.payload;
      state[userId].isPopoverOpen = false;
    },
  },
});

export const { startTimer, endTimer, setProject, setTask, openPopoverAt, closePopover } = timeTrackerSlice.actions;
export default timeTrackerSlice.reducer;
